import { useState, useEffect } from 'react';
import { useTheme } from '@/hooks/useTheme';
import ThemeToggle from './ThemeToggle';

interface NavbarProps {
  onBookAudit: () => void;
}

export default function Navbar({ onBookAudit }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '#hero' },
    { label: 'Services', href: '#services' },
    { label: 'Industries', href: '#industries' },
    { label: 'Process', href: '#process' },
    { label: 'Contact', href: '#cta' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? isDark
            ? 'bg-[#0E1116]/90 backdrop-blur-xl border-b border-white/8'
            : 'bg-white/90 backdrop-blur-xl border-b border-gray-200/50 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-[90px]">
          {/* Logo */}
          <a href="#hero" className="flex items-center cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/a6aaf78b-ae52-4def-a2c6-79fec2f6b030.png"
              alt="PuleAI Logo"
              className="object-contain"
              style={{ width: '90px', height: '90px' }}
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm transition-colors duration-200 tracking-wide cursor-pointer whitespace-nowrap ${
                  isDark
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA + Theme Toggle */}
          <div className="hidden md:flex items-center gap-4">
            <ThemeToggle />
            <button
              onClick={onBookAudit}
              className={`text-sm font-medium px-5 py-2.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap tracking-wide ${
                isDark
                  ? 'bg-white text-[#0E1116] hover:bg-gray-100'
                  : 'bg-[#0E1116] text-white hover:bg-gray-800'
              }`}
            >
              Book Audit
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className={`md:hidden border-t px-6 py-4 ${
          isDark
            ? 'bg-[#0E1116]/98 border-white/5'
            : 'bg-white/98 border-gray-200/50'
        }`}>
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`text-sm transition-colors py-1 cursor-pointer ${
                  isDark
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center justify-between mt-2 pt-4 border-t border-white/5">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Theme</span>
              <ThemeToggle />
            </div>
            <button
              onClick={() => { setMobileOpen(false); onBookAudit(); }}
              className={`mt-2 text-sm font-medium px-5 py-2.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap w-full ${
                isDark
                  ? 'bg-white text-[#0E1116] hover:bg-gray-100'
                  : 'bg-[#0E1116] text-white hover:bg-gray-800'
              }`}
            >
              Book Audit
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}